import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { LedgerPosting } from 'api/types';

interface PostingViewModalProps {
  isOpen: boolean;
  posting?: LedgerPosting | null;
  onClose: () => void;
  /** Rendered only when the user holds the 'retry' action and the row is retryable. */
  onRetry?: (posting: LedgerPosting) => void;
  canRetry?: boolean;
}

const prettyJson = (raw?: string): string => {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
};

const formatTs = (ts?: string): string => (ts ? new Date(ts).toLocaleString() : '-');

const STATUS_BADGE: Record<string, string> = {
  POSTED: 'badge-active',
  PENDING: 'badge-pending',
  PUBLISHED: 'badge-pending',
  FAILED: 'badge-error',
  MANUAL_REVIEW: 'badge-error',
  SKIPPED: 'badge-inactive',
};

/**
 * Diagnostic view of one posting: the exact JSON sent, the provider's reply,
 * and the failure reason when parked. Read-only apart from Retry.
 */
const PostingViewModal: React.FC<PostingViewModalProps> = ({ isOpen, posting, onClose, onRetry, canRetry = false }) => {
  const requestJson = useMemo(() => prettyJson(posting?.requestPayload), [posting]);
  const responseJson = useMemo(() => prettyJson(posting?.responsePayload), [posting]);

  if (!isOpen || !posting) return null;

  const retryable = canRetry && posting.status !== 'POSTED';

  const metaRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.82rem', padding: '0.15rem 0' }}>
      <span style={{ color: 'var(--text-muted)', minWidth: '130px' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Posting Details
            <span className={`badge ${STATUS_BADGE[posting.status] || 'badge-pending'}`}>{posting.status}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="form-grid-2">
            <div>
              {metaRow('Reference', <span style={{ fontFamily: 'monospace' }}>{posting.idempotencyKey || '-'}</span>)}
              {metaRow('Type', `${posting.integrationType} / ${posting.sourceType}`)}
              {metaRow('Fairbooks Ref', posting.externalReference || '-')}
              {metaRow('Attempts', posting.attemptCount)}
            </div>
            <div>
              {metaRow('Created', formatTs(posting.createdAt))}
              {metaRow('Last Attempt', formatTs(posting.lastAttemptAt))}
              {metaRow('Processed', formatTs(posting.processedAt))}
              {metaRow('Next Attempt', formatTs(posting.nextAttemptAt))}
            </div>
          </div>

          {posting.lastError && (
            <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '0.375rem', background: 'var(--danger-bg, rgba(220,38,38,0.08))', color: 'var(--danger, #dc2626)', fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>
              <strong>Last error:</strong> {posting.lastError}
            </div>
          )}

          <div style={{ marginTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>Request Sent</h3>
            <pre style={{ fontSize: '0.75rem', fontFamily: 'monospace', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem', padding: '0.6rem', maxHeight: '220px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--text-primary)' }}>
              {requestJson || '(not built yet)'}
            </pre>
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>Provider Response</h3>
            <pre style={{ fontSize: '0.75rem', fontFamily: 'monospace', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem', padding: '0.6rem', maxHeight: '220px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--text-primary)' }}>
              {responseJson || '(no response yet)'}
            </pre>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {retryable && onRetry && (
            <button className="btn btn-primary" onClick={() => onRetry(posting)}>
              <IconSystem.refresh size={16} />
              Retry Posting
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default PostingViewModal;
